<script lang="ts">
  // My Drafts tab per INTERFACE-SPECIFICATION.md §8.3.
  //
  // Active drafts: list cards for each draft branch on the user's
  // namespace. Each card shows slug, jurisdiction-product, staged-
  // change count, last-edited timestamp, conflict status, and three
  // actions (Resume editing / Submit for review / Discard). Archive
  // section lists closed drafts within the 90-day recovery window.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { navigate } from '../lib/router.svelte';
  import { findProduct } from '../lib/products';
  import { formatLocalDate, relativeTimeAgo } from '../lib/formatters';
  import { cachedCatalogue, fetchCatalogue } from '../lib/catalogue-store.svelte';
  import { adoptDraftFromServer } from '../lib/staged-edits.svelte';
  import type { ListDraftsResponse, DraftSummary } from '../lib/types';
  import SwipeToConfirm from './SwipeToConfirm.svelte';

  let response = $state<ListDraftsResponse | null>(null);
  let error = $state<string | null>(null);
  let loading = $state<boolean>(true);

  onMount(() => {
    void load();
  });

  async function load() {
    loading = true;
    error = null;
    try {
      response = await api.listDrafts();
    } catch (err) {
      error = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'list_drafts_failed';
    } finally {
      loading = false;
    }
  }

  async function discard(slug: string) {
    if (typeof window === 'undefined') return;
    // Spec §8.3: closed drafts cannot be restored. The confirm copy
    // makes the irreversibility explicit so the proposer is not
    // surprised by the read-only Archive.
    const confirmed = window.confirm(
      `Discard draft "${slug}"?\n\n` +
      'Closed drafts cannot be restored. The archived entry will appear ' +
      'in your Archive as read-only history for 90 days. You can begin ' +
      'a new draft from its content via "Start new draft from this".'
    );
    if (!confirmed) return;
    try {
      await api.discardDraft(slug);
      await load();
    } catch (err) {
      error = err instanceof Error ? err.message : 'discard_failed';
    }
  }

  async function startNewDraftFromArchive(archivedSlug: string) {
    if (typeof window === 'undefined') return;
    // Pre-fill with a sensible default new slug. The proposer can edit
    // it; the server enforces uniqueness against any live draft.
    const defaultSlug = `${archivedSlug}-take-2`;
    const newSlug = window.prompt(
      `Start a new draft from the archived "${archivedSlug}".\n\n` +
      'The new draft will be rooted at current main and pre-filled ' +
      'with the archived staged changes. The archived entry stays in ' +
      'place as read-only history.\n\n' +
      'Slug for the new draft:',
      defaultSlug
    );
    if (!newSlug) return;
    error = null;
    try {
      await api.newDraftFromArchive(archivedSlug, newSlug);
      // Reload so the new draft appears in the Active drafts section.
      // A future enhancement could navigate directly into the new
      // draft's editor — would need to surface jurisdiction/product
      // from the response so the route can be composed.
      await load();
    } catch (err) {
      error = err instanceof Error ? err.message : 'new_draft_from_archive_failed';
    }
  }

  async function resume(draft: DraftSummary) {
    // Hydrate the staged-edits store from the server-side draft before
    // navigating to the editor. The CatalogueDetail mount runs
    // loadFromSession; without server hydration, an operator who
    // cleared their session (cross-device, fresh login, sessionStorage
    // eviction) lands on an empty editor and any new edit creates a
    // different default-slug draft. The re-author flow already uses
    // this pattern via adoptDraftFromServer; the generic Resume path
    // reuses the same helper.
    error = null;
    try {
      const catalogue = cachedCatalogue(draft.jurisdiction, draft.product)
        ?? await fetchCatalogue(draft.jurisdiction, draft.product);
      if (catalogue) {
        await adoptDraftFromServer(draft.draft_slug, catalogue);
      }
      navigate(`/catalogue/detail/${draft.jurisdiction}/${draft.product}`);
    } catch (err) {
      error = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'resume_failed';
    }
  }

  function productLabel(draft: DraftSummary): string {
    const desc = findProduct(draft.jurisdiction, draft.product);
    return desc ? `${desc.label} · ${desc.customerLabel}` : `${draft.jurisdiction}/${draft.product}`;
  }
</script>

<section>
  {#if loading}
    <p class="muted">Loading drafts…</p>
  {:else if error}
    <div class="banner banner-fail">{error}</div>
  {:else if !response || response.active.length + response.archive.length === 0}
    <p class="muted">You have no drafts. Open Catalogue → Detail and click a cube cell to stage your first edit.</p>
  {:else}
    {#if response.active.length > 0}
      <h3 class="section-heading">Active drafts</h3>
      <ul class="draft-list">
        {#each response.active as draft (draft.draft_slug)}
          <li class="draft-card">
            <header>
              <strong>{draft.draft_slug}</strong>
              <span class="muted">{productLabel(draft)}</span>
              <span class="muted">{draft.staged_change_count} staged · last edited {relativeTimeAgo(draft.last_edited_at)}</span>
            </header>
            {#if draft.conflict_status === 'in_conflict'}
              <div class="banner banner-fail">
                Your draft conflicts with a recently merged change. Open the draft, review the merged state, and reconcile.
              </div>
            {:else if draft.quiet_days >= 23}
              <div class="banner banner-warn">
                This draft has been quiet for {draft.quiet_days} days. The draft-cleanup job will auto-close it after 30.
              </div>
            {/if}
            <div class="draft-card__actions">
              <button class="btn" type="button" onclick={() => resume(draft)}>Resume editing</button>
              <div class="desktop-only">
                <button class="btn" type="button" onclick={() => discard(draft.draft_slug)}>Discard</button>
              </div>
              <div class="mobile-only draft-card__discard">
                <SwipeToConfirm
                  label="Discard draft"
                  confirmingLabel="Discarding…"
                  onConfirm={() => discard(draft.draft_slug)}
                />
              </div>
            </div>
          </li>
        {/each}
      </ul>
    {/if}

    {#if response.archive.length > 0}
      <h3 class="section-heading" style="margin-top: var(--space-5)">Archive · read-only history (retained 90 days)</h3>
      <p class="muted" style="font-size: var(--type-sm); margin: 0 0 var(--space-2) 0;">
        Each entry is read-only. To begin a new draft based on an
        archived entry, use [Start new draft from this] — a new draft
        branch is created from current main, pre-filled with the
        archived entry's staged changes. The archived entry itself is
        not restored or modified.
      </p>
      <ul class="draft-list">
        {#each response.archive as entry (entry.archive_branch)}
          <li class="draft-card">
            <span><strong>{entry.draft_slug}</strong></span>
            <span class="muted">Closed {entry.closed_at ? formatLocalDate(entry.closed_at) : ''}</span>
            <button class="btn" type="button" onclick={() => startNewDraftFromArchive(entry.draft_slug)}>
              Start new draft from this
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</section>

<style>
  .draft-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }
  .draft-card {
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .draft-card header {
    display: flex;
    gap: var(--space-3);
    align-items: baseline;
    flex-wrap: wrap;
  }
  .draft-card__actions {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-2);
  }
</style>
